/**
 * HealthBar - Minecraft-style health and hunger HUD
 *
 * Renders above the hotbar:
 *   Left side:  10 hearts (each = 2 HP)
 *   Right side: 10 drumsticks (each = 2 hunger)
 *   Center:     XP bar (visual only, level 0)
 *
 * Also handles damage flash overlay and death screen.
 */
export class HealthBar {
  constructor(health) {
    this.health = health;
    this._createStyles();
    this._createContainer();
    this._createBubbles();
    this._createHearts();
    this._createHunger();
    this._createXPBar();
    this._createDamageOverlay();
    this._createDeathScreen();
  }

  // ─── CSS injection ─────────────────────────────────────────
  _createStyles() {
    if (document.getElementById('healthbar-styles')) return;
    const style = document.createElement('style');
    style.id = 'healthbar-styles';
    style.textContent = `
      /* ---- Heart icon (CSS-only) ---- */
      .hb-heart {
        position: relative;
        width: 16px; height: 14px;
        display: inline-block;
        margin: 0 1px;
        image-rendering: pixelated;
      }
      /* Background (empty heart outline) */
      .hb-heart::before {
        content: '';
        position: absolute; inset: 0;
        background: #3a0a0a;
        clip-path: path('M8 13 Q0 8 0 4.5 Q0 0 4 0 Q6 0 8 3 Q10 0 12 0 Q16 0 16 4.5 Q16 8 8 13Z');
      }
      /* Filled portion */
      .hb-heart-fill {
        position: absolute; inset: 0;
        clip-path: path('M8 13 Q0 8 0 4.5 Q0 0 4 0 Q6 0 8 3 Q10 0 12 0 Q16 0 16 4.5 Q16 8 8 13Z');
      }
      .hb-heart-fill.full  { background: #e02020; }
      .hb-heart-fill.half  { background: linear-gradient(to right, #e02020 50%, transparent 50%); }
      .hb-heart-fill.empty { background: transparent; }
      /* Highlight on full/half hearts */
      .hb-heart-fill.full::after,
      .hb-heart-fill.half::after {
        content: '';
        position: absolute;
        top: 2px; left: 2px;
        width: 5px; height: 4px;
        background: rgba(255,150,150,0.45);
        border-radius: 50%;
      }

      /* ---- Drumstick icon (CSS-only) ---- */
      .hb-drum {
        position: relative;
        width: 16px; height: 14px;
        display: inline-block;
        margin: 0 1px;
        image-rendering: pixelated;
      }
      .hb-drum::before {
        content: '';
        position: absolute; inset: 0;
        background: #2a1a06;
        clip-path: polygon(
          30% 0%, 70% 0%, 80% 10%, 90% 25%, 95% 45%,
          85% 60%, 70% 65%, 55% 75%, 45% 90%, 35% 100%,
          25% 100%, 20% 90%, 30% 75%, 20% 65%,
          10% 55%, 5% 40%, 10% 20%, 20% 5%
        );
      }
      .hb-drum-fill {
        position: absolute; inset: 0;
        clip-path: polygon(
          30% 0%, 70% 0%, 80% 10%, 90% 25%, 95% 45%,
          85% 60%, 70% 65%, 55% 75%, 45% 90%, 35% 100%,
          25% 100%, 20% 90%, 30% 75%, 20% 65%,
          10% 55%, 5% 40%, 10% 20%, 20% 5%
        );
      }
      .hb-drum-fill.full  { background: #c89040; }
      .hb-drum-fill.half  { background: linear-gradient(to right, #c89040 50%, transparent 50%); }
      .hb-drum-fill.empty { background: transparent; }
      .hb-drum-fill.full::after,
      .hb-drum-fill.half::after {
        content: '';
        position: absolute;
        top: 2px; left: 4px;
        width: 5px; height: 4px;
        background: rgba(255,220,160,0.35);
        border-radius: 50%;
      }

      /* ---- XP bar ---- */
      .hb-xp-bar {
        height: 5px;
        background: #1a1a1a;
        border: 1px solid rgba(0,0,0,0.5);
        border-radius: 1px;
        overflow: hidden;
        image-rendering: pixelated;
      }
      .hb-xp-bar-fill {
        height: 100%;
        background: #80e820;
        width: 0%;
        transition: width 0.3s;
      }
      .hb-xp-level {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 0px;
        font-size: 10px;
        font-family: 'Courier New', monospace;
        color: #80e820;
        text-shadow: 1px 1px 0 #000, -1px 0 0 #000, 0 -1px 0 #000, 1px 0 0 #000;
        font-weight: bold;
        pointer-events: none;
      }

      /* ---- Damage flash ---- */
      .hb-damage-overlay {
        position: fixed;
        inset: 0;
        background: rgba(180, 0, 0, 0.35);
        pointer-events: none;
        z-index: 200;
        opacity: 0;
        transition: opacity 0.05s;
      }

      /* ---- Death screen ---- */
      .hb-death-screen {
        position: fixed;
        inset: 0;
        background: rgba(120, 10, 10, 0.72);
        z-index: 500;
        display: none;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: 'Courier New', monospace;
        image-rendering: pixelated;
        opacity: 0;
        transition: opacity 0.5s ease-in;
      }
      .hb-death-screen.visible {
        opacity: 1;
      }
      .hb-death-title {
        font-size: 64px;
        color: #fff;
        text-shadow:
          0 0 10px rgba(255, 40, 40, 0.9),
          0 0 20px rgba(255, 40, 40, 0.6),
          0 0 40px rgba(255, 0, 0, 0.4),
          3px 3px 0 #3F3F3F,
          -2px -2px 0 #3F3F3F;
        margin-bottom: 12px;
        letter-spacing: 5px;
        font-weight: bold;
        text-transform: uppercase;
      }
      .hb-death-subtitle {
        font-size: 22px;
        color: rgba(255, 200, 200, 0.85);
        text-shadow: 2px 2px 0 rgba(0,0,0,0.6);
        margin-bottom: 30px;
        letter-spacing: 1px;
      }
      .hb-death-stats {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        margin-bottom: 36px;
        padding: 16px 32px;
        background: rgba(0, 0, 0, 0.35);
        border: 2px solid rgba(255, 255, 255, 0.12);
        border-radius: 2px;
      }
      .hb-death-stat {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.8);
        text-shadow: 1px 1px 0 #000;
        letter-spacing: 1px;
      }
      .hb-death-stat-value {
        color: #ffd700;
        font-weight: bold;
      }
      .hb-death-btn {
        padding: 12px 48px;
        font-size: 22px;
        font-family: 'Courier New', monospace;
        color: white;
        background: linear-gradient(to bottom, #6a6a6a 0%, #4a4a4a 50%, #3a3a3a 50%, #505050 100%);
        border: none;
        border-top: 3px solid rgba(255,255,255,0.25);
        border-left: 3px solid rgba(255,255,255,0.18);
        border-right: 3px solid rgba(0,0,0,0.35);
        border-bottom: 3px solid rgba(0,0,0,0.5);
        cursor: pointer;
        letter-spacing: 2px;
        text-shadow: 2px 2px 0 #2a2a2a;
        image-rendering: pixelated;
        outline: 2px solid rgba(0,0,0,0.4);
        transition: filter 0.1s;
      }
      .hb-death-btn:hover {
        filter: brightness(1.25);
        border-top-color: rgba(255,255,255,0.4);
        border-left-color: rgba(255,255,255,0.3);
      }
      .hb-death-btn:active {
        border-top: 3px solid rgba(0,0,0,0.35);
        border-left: 3px solid rgba(0,0,0,0.25);
        border-right: 3px solid rgba(255,255,255,0.15);
        border-bottom: 3px solid rgba(255,255,255,0.2);
        filter: brightness(0.9);
      }

      /* ---- Death screen red vignette pulse ---- */
      @keyframes hb-vignette-pulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.6; }
      }
      .hb-death-vignette {
        position: absolute; inset: 0;
        background: radial-gradient(ellipse at center, transparent 40%, rgba(120, 0, 0, 0.7) 100%);
        animation: hb-vignette-pulse 3s ease-in-out infinite;
        pointer-events: none;
        z-index: 0;
      }

      /* ---- Death screen cause of death ---- */
      .hb-death-cause {
        font-size: 18px;
        color: rgba(255, 160, 160, 0.9);
        text-shadow: 2px 2px 0 rgba(0,0,0,0.6);
        margin-bottom: 20px;
        letter-spacing: 1px;
        font-style: italic;
      }

      /* ---- Death particles (CSS animated dots) ---- */
      @keyframes hb-death-particle-fall {
        0% { transform: translateY(-20px); opacity: 0; }
        10% { opacity: 0.6; }
        100% { transform: translateY(100vh); opacity: 0; }
      }
      .hb-death-particles {
        position: absolute; inset: 0;
        overflow: hidden;
        pointer-events: none;
        z-index: 0;
      }
      .hb-death-particle {
        position: absolute;
        width: 3px; height: 3px;
        background: rgba(200, 40, 40, 0.5);
        border-radius: 50%;
        animation: hb-death-particle-fall linear infinite;
      }

      /* ---- Low-health wobble for hearts ---- */
      @keyframes hb-heart-shake {
        0%, 100% { transform: translateY(0); }
        25%      { transform: translateY(-2px); }
        75%      { transform: translateY(1px); }
      }
      .hb-heart.shake {
        animation: hb-heart-shake 0.25s ease-in-out infinite;
      }

      /* ---- Air bubbles (drowning) ---- */
      .hb-bubbles-row {
        display: flex;
        justify-content: flex-end;
        width: 180px;
        direction: rtl;
        margin-bottom: 2px;
        height: 16px;
      }
      .hb-bubble {
        position: relative;
        width: 14px; height: 14px;
        display: inline-block;
        margin: 0 1px;
      }
      /* Burbuja vacia (fondo) */
      .hb-bubble::before {
        content: '';
        position: absolute; inset: 0;
        background: rgba(30, 60, 100, 0.35);
        clip-path: circle(50% at 50% 50%);
      }
      /* Burbuja llena */
      .hb-bubble-fill {
        position: absolute; inset: 0;
        clip-path: circle(50% at 50% 50%);
        transition: opacity 0.15s;
      }
      .hb-bubble-fill.full {
        background: radial-gradient(circle at 35% 35%, rgba(200, 230, 255, 0.9), rgba(80, 160, 220, 0.7));
      }
      .hb-bubble-fill.empty {
        opacity: 0;
        background: transparent;
      }
      /* Reflejo en burbuja llena */
      .hb-bubble-fill.full::after {
        content: '';
        position: absolute;
        top: 2px; left: 3px;
        width: 4px; height: 3px;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 50%;
      }
      /* Animacion de pop cuando burbuja se vacia */
      @keyframes hb-bubble-pop {
        0%   { transform: scale(1); opacity: 1; }
        50%  { transform: scale(1.3); opacity: 0.5; }
        100% { transform: scale(0); opacity: 0; }
      }
      .hb-bubble-fill.popping {
        animation: hb-bubble-pop 0.3s ease-out forwards;
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Main container (sits above the hotbar) ────────────────
  _createContainer() {
    // The hotbar is ~52px tall at bottom:4px, so our container bottom ~60px
    this.container = document.createElement('div');
    this.container.id = 'healthbar-hud';
    this.container.style.cssText = `
      position: fixed;
      bottom: 62px;
      left: 50%;
      transform: translateX(-50%);
      width: 364px;
      pointer-events: none;
      z-index: 100;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 2px;
    `;
    document.body.appendChild(this.container);

    // Row for hearts + hunger (side by side)
    this.statsRow = document.createElement('div');
    this.statsRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      width: 100%;
    `;
    this.container.appendChild(this.statsRow);

    // Armor bar row (above hearts, same width)
    this.armorRow = document.createElement('div');
    this.armorRow.style.cssText = `
      display: none;
      width: 180px;
      gap: 1px;
      margin-bottom: 2px;
    `;
    // Insert armor row before stats row
    this.container.insertBefore(this.armorRow, this.statsRow);

    this.armorIcons = [];
    for (let i = 0; i < 10; i++) {
      const icon = document.createElement('div');
      icon.style.cssText = `
        width: 16px; height: 14px;
        display: inline-block;
        position: relative;
        image-rendering: pixelated;
      `;
      // Shield outline (dark)
      const bg = document.createElement('div');
      bg.style.cssText = `
        position: absolute; inset: 0;
        background: #3a3a3a;
        clip-path: polygon(50% 0%, 100% 25%, 100% 60%, 50% 100%, 0% 60%, 0% 25%);
      `;
      icon.appendChild(bg);
      // Shield fill
      const fill = document.createElement('div');
      fill.className = 'armor-fill';
      fill.style.cssText = `
        position: absolute; inset: 0;
        background: #C8C8C8;
        clip-path: polygon(50% 0%, 100% 25%, 100% 60%, 50% 100%, 0% 60%, 0% 25%);
      `;
      icon.appendChild(fill);
      this.armorRow.appendChild(icon);
      this.armorIcons.push({ icon, fill });
    }
  }

  // ─── Hearts ────────────────────────────────────────────────
  _createHearts() {
    this.heartsContainer = document.createElement('div');
    this.heartsContainer.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      width: 180px;
      justify-content: flex-start;
    `;
    this.statsRow.appendChild(this.heartsContainer);

    this.heartEls = [];
    for (let i = 0; i < 10; i++) {
      const heart = document.createElement('div');
      heart.className = 'hb-heart';
      const fill = document.createElement('div');
      fill.className = 'hb-heart-fill full';
      heart.appendChild(fill);
      this.heartsContainer.appendChild(heart);
      this.heartEls.push({ root: heart, fill });
    }
  }

  // ─── Hunger drumsticks ─────────────────────────────────────
  _createHunger() {
    this.hungerContainer = document.createElement('div');
    this.hungerContainer.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      width: 180px;
      justify-content: flex-end;
      direction: rtl;
    `;
    this.statsRow.appendChild(this.hungerContainer);

    this.drumEls = [];
    for (let i = 0; i < 10; i++) {
      const drum = document.createElement('div');
      drum.className = 'hb-drum';
      drum.style.direction = 'ltr';
      const fill = document.createElement('div');
      fill.className = 'hb-drum-fill full';
      drum.appendChild(fill);
      this.hungerContainer.appendChild(drum);
      this.drumEls.push({ root: drum, fill });
    }
  }

  // ─── Air bubbles (drowning indicator) ─────────────────────
  _createBubbles() {
    // Row for bubbles - sits above the hunger drumsticks (right side)
    this.bubblesRow = document.createElement('div');
    this.bubblesRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      width: 100%;
      height: 0px;
      overflow: visible;
      transition: height 0.2s;
    `;

    // Empty left spacer (same width as hearts)
    const leftSpacer = document.createElement('div');
    leftSpacer.style.width = '180px';
    this.bubblesRow.appendChild(leftSpacer);

    // Bubbles container (right side, above hunger)
    this.bubblesContainer = document.createElement('div');
    this.bubblesContainer.className = 'hb-bubbles-row';
    this.bubblesRow.appendChild(this.bubblesContainer);

    // Insert before statsRow so it appears above hearts/hunger
    this.container.insertBefore(this.bubblesRow, this.statsRow);

    this.bubbleEls = [];
    for (let i = 0; i < 10; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'hb-bubble';
      bubble.style.direction = 'ltr';
      const fill = document.createElement('div');
      fill.className = 'hb-bubble-fill full';
      bubble.appendChild(fill);
      this.bubblesContainer.appendChild(bubble);
      this.bubbleEls.push({ root: bubble, fill });
    }

    // Track previous bubble states for pop animation
    this._prevBubbleStates = new Array(10).fill(true); // true = full
    this._bubblesVisible = false;
  }

  // ─── XP bar ────────────────────────────────────────────────
  _createXPBar() {
    this.xpWrap = document.createElement('div');
    this.xpWrap.style.cssText = 'position: relative; width: 100%;';

    this.xpBar = document.createElement('div');
    this.xpBar.className = 'hb-xp-bar';
    this.xpBarFill = document.createElement('div');
    this.xpBarFill.className = 'hb-xp-bar-fill';
    this.xpBarFill.style.width = '0%';
    this.xpBar.appendChild(this.xpBarFill);
    this.xpWrap.appendChild(this.xpBar);

    this.xpLevel = document.createElement('div');
    this.xpLevel.className = 'hb-xp-level';
    this.xpLevel.textContent = '0';
    this.xpWrap.appendChild(this.xpLevel);

    this.container.appendChild(this.xpWrap);
  }

  // ─── Damage flash overlay ──────────────────────────────────
  _createDamageOverlay() {
    this.damageOverlay = document.createElement('div');
    this.damageOverlay.className = 'hb-damage-overlay';
    document.body.appendChild(this.damageOverlay);
  }

  // ─── Death screen ──────────────────────────────────────────
  _createDeathScreen() {
    this.deathScreen = document.createElement('div');
    this.deathScreen.className = 'hb-death-screen';

    // Background video (loops silently behind death screen)
    this._deathVideo = document.createElement('video');
    this._deathVideo.style.cssText = `
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      opacity: 0.35;
      z-index: -1;
      pointer-events: none;
    `;
    this._deathVideo.loop = true;
    this._deathVideo.muted = true;
    this._deathVideo.playsInline = true;
    this._deathVideo.preload = 'auto';
    // Check if video exists before setting src
    fetch('/death.webm', { method: 'HEAD' })
      .then(r => {
        if (r.ok && r.headers.get('content-type')?.startsWith('video/')) {
          this._deathVideo.src = '/death.webm';
        }
      })
      .catch(() => {});
    this.deathScreen.appendChild(this._deathVideo);

    // Vignette overlay
    this._deathVignette = document.createElement('div');
    this._deathVignette.className = 'hb-death-vignette';
    this.deathScreen.appendChild(this._deathVignette);

    // Particle container
    this._deathParticles = document.createElement('div');
    this._deathParticles.className = 'hb-death-particles';
    this.deathScreen.appendChild(this._deathParticles);

    const title = document.createElement('div');
    title.className = 'hb-death-title';
    title.textContent = '\u00a1HAS MUERTO!';
    title.style.zIndex = '1';

    // Cause of death line
    this.deathCause = document.createElement('div');
    this.deathCause.className = 'hb-death-cause';
    this.deathCause.style.display = 'none';
    this.deathCause.style.zIndex = '1';

    this.deathSubtitle = document.createElement('div');
    this.deathSubtitle.className = 'hb-death-subtitle';
    this.deathSubtitle.style.display = 'none';
    this.deathSubtitle.style.zIndex = '1';

    this.deathStats = document.createElement('div');
    this.deathStats.className = 'hb-death-stats';
    this.deathStats.style.zIndex = '1';

    this.statBlocksMined = document.createElement('div');
    this.statBlocksMined.className = 'hb-death-stat';
    this.statMobsKilled = document.createElement('div');
    this.statMobsKilled.className = 'hb-death-stat';
    this.statDaysSurvived = document.createElement('div');
    this.statDaysSurvived.className = 'hb-death-stat';

    this.deathStats.appendChild(this.statBlocksMined);
    this.deathStats.appendChild(this.statMobsKilled);
    this.deathStats.appendChild(this.statDaysSurvived);

    this.respawnBtn = document.createElement('button');
    this.respawnBtn.className = 'hb-death-btn';
    this.respawnBtn.textContent = 'Reaparecer';
    this.respawnBtn.style.zIndex = '1';

    this.deathScreen.appendChild(title);
    this.deathScreen.appendChild(this.deathCause);
    this.deathScreen.appendChild(this.deathSubtitle);
    this.deathScreen.appendChild(this.deathStats);
    this.deathScreen.appendChild(this.respawnBtn);
    document.body.appendChild(this.deathScreen);
  }

  // ─── Public: refresh display every frame ───────────────────
  update() {
    const hp = this.health.health;
    const maxHp = this.health.maxHealth;
    const hun = this.health.hunger;
    const lowHealth = hp <= 6 && hp > 0;

    // --- Hearts ---
    for (let i = 0; i < 10; i++) {
      const { root, fill } = this.heartEls[i];
      const pointsForThisHeart = hp - i * 2;
      if (pointsForThisHeart >= 2) {
        fill.className = 'hb-heart-fill full';
      } else if (pointsForThisHeart === 1) {
        fill.className = 'hb-heart-fill half';
      } else {
        fill.className = 'hb-heart-fill empty';
      }
      // Shake when low health
      if (lowHealth) {
        root.classList.add('shake');
        root.style.animationDelay = `${i * 0.04}s`;
      } else {
        root.classList.remove('shake');
      }
    }

    // --- Hunger drumsticks ---
    for (let i = 0; i < 10; i++) {
      const { fill } = this.drumEls[i];
      const pointsForThisDrum = hun - i * 2;
      if (pointsForThisDrum >= 2) {
        fill.className = 'hb-drum-fill full';
      } else if (pointsForThisDrum === 1) {
        fill.className = 'hb-drum-fill half';
      } else {
        fill.className = 'hb-drum-fill empty';
      }
    }

    // --- Air bubbles (drowning) ---
    const underwater = this.health.isUnderwater();
    if (underwater && !this._bubblesVisible) {
      this.bubblesRow.style.height = '18px';
      this._bubblesVisible = true;
    } else if (!underwater && this._bubblesVisible) {
      this.bubblesRow.style.height = '0px';
      this._bubblesVisible = false;
      // Reset all bubbles to full when surfacing
      for (let i = 0; i < 10; i++) {
        this.bubbleEls[i].fill.className = 'hb-bubble-fill full';
        this._prevBubbleStates[i] = true;
      }
    }

    if (this._bubblesVisible) {
      const airSupply = this.health.airSupply;
      // Each bubble = 30 air points (300 total / 10 bubbles)
      for (let i = 0; i < 10; i++) {
        const threshold = (i + 1) * 30; // bubble i is full if airSupply >= threshold
        const isFull = airSupply >= threshold;
        const { fill } = this.bubbleEls[i];

        if (isFull && !this._prevBubbleStates[i]) {
          // Bubble restored
          fill.className = 'hb-bubble-fill full';
        } else if (!isFull && this._prevBubbleStates[i]) {
          // Bubble just popped
          fill.className = 'hb-bubble-fill popping';
          // After animation, set to empty
          setTimeout(() => {
            if (fill.className === 'hb-bubble-fill popping') {
              fill.className = 'hb-bubble-fill empty';
            }
          }, 300);
        }
        this._prevBubbleStates[i] = isFull;
      }
    }

    // --- Damage flash overlay ---
    if (this.health.damageFlash > 0) {
      this.damageOverlay.style.opacity = Math.min(1, this.health.damageFlash / 0.15).toString();
    } else {
      this.damageOverlay.style.opacity = '0';
    }

    // --- XP bar from XPSystem ---
    if (this._xpSystem) {
      this.setXP(this._xpSystem.getProgress(), this._xpSystem.level);
    }

    // --- Armor display ---
    this._updateArmor();

    // --- Death screen auto-show ---
    if (this.health.dead && this.deathScreen.style.display !== 'flex') {
      this.deathScreen.style.display = 'flex';
      this.deathScreen.classList.remove('visible');
      void this.deathScreen.offsetWidth;
      this.deathScreen.classList.add('visible');
    }
  }

  // ─── Show death screen with respawn callback ───────────────
  showDeathScreen(onRespawn, stats) {
    const s = stats || {};

    // Cause of death display
    if (s.causeOfDeath) {
      this.deathCause.textContent = s.causeOfDeath;
      this.deathCause.style.display = 'block';
    } else {
      this.deathCause.style.display = 'none';
    }

    // Show/hide subtitle
    if (s.subtitle) {
      this.deathSubtitle.textContent = s.subtitle;
      this.deathSubtitle.style.display = 'block';
    } else {
      this.deathSubtitle.style.display = 'none';
    }

    // Initialize stats at 0 (will animate up)
    this.statBlocksMined.innerHTML = `Bloques minados: <span class="hb-death-stat-value">0</span>`;
    this.statMobsKilled.innerHTML = `Mobs eliminados: <span class="hb-death-stat-value">0</span>`;
    this.statDaysSurvived.innerHTML = `D\u00edas sobrevividos: <span class="hb-death-stat-value">0</span>`;

    // Show with fade-in animation
    this.deathScreen.style.display = 'flex';
    this.deathScreen.classList.remove('visible');
    void this.deathScreen.offsetWidth;
    this.deathScreen.classList.add('visible');

    // Create falling particles
    this._spawnDeathParticles();

    // Animate stats counting up (after a short delay for dramatic effect)
    setTimeout(() => {
      this._animateStatCount(this.statBlocksMined, 'Bloques minados', s.blocksMined ?? 0, 1200);
      this._animateStatCount(this.statMobsKilled, 'Mobs eliminados', s.mobsKilled ?? 0, 1000);
      this._animateStatCount(this.statDaysSurvived, 'D\u00edas sobrevividos', s.daysSurvived ?? 0, 800);
    }, 600);

    // Play death background video
    if (this._deathVideo && this._deathVideo.src) {
      this._deathVideo.currentTime = 0;
      this._deathVideo.play().catch(() => {});
    }

    // Remove old listeners by replacing the button
    const newBtn = this.respawnBtn.cloneNode(true);
    this.respawnBtn.replaceWith(newBtn);
    this.respawnBtn = newBtn;
    this.respawnBtn.addEventListener('click', () => {
      this.hideDeathScreen();
      if (onRespawn) onRespawn();
    });
  }

  /** Animate a stat element counting from 0 to target */
  _animateStatCount(el, label, target, duration) {
    if (target <= 0) {
      el.innerHTML = `${label}: <span class="hb-death-stat-value">0</span>`;
      return;
    }
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / duration);
      // Ease-out curve
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      el.innerHTML = `${label}: <span class="hb-death-stat-value">${current}</span>`;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /** Create falling particle dots for death screen */
  _spawnDeathParticles() {
    // Clear old particles
    this._deathParticles.innerHTML = '';
    const count = 30;
    for (let i = 0; i < count; i++) {
      const p = document.createElement('div');
      p.className = 'hb-death-particle';
      p.style.left = `${Math.random() * 100}%`;
      p.style.top = `${Math.random() * 20 - 10}%`;
      p.style.animationDuration = `${3 + Math.random() * 5}s`;
      p.style.animationDelay = `${Math.random() * 4}s`;
      p.style.width = `${2 + Math.random() * 3}px`;
      p.style.height = p.style.width;
      p.style.opacity = `${0.2 + Math.random() * 0.4}`;
      this._deathParticles.appendChild(p);
    }
  }

  // ─── Hide death screen ─────────────────────────────────────
  hideDeathScreen() {
    this.deathScreen.classList.remove('visible');
    this.deathScreen.style.display = 'none';
    if (this._deathVideo) {
      this._deathVideo.pause();
    }
    // Clean up particles
    if (this._deathParticles) {
      this._deathParticles.innerHTML = '';
    }
  }

  // ─── Connect Armor system for durability display ────────────
  setArmorSystem(armorSystem) {
    this._armorSystem = armorSystem;
  }

  // ─── Update armor icons ────────────────────────────────────
  _updateArmor() {
    if (!this._armorSystem) {
      this.armorRow.style.display = 'none';
      return;
    }
    const points = this._armorSystem.getArmorPoints();
    if (points <= 0) {
      this.armorRow.style.display = 'none';
      return;
    }
    this.armorRow.style.display = 'flex';

    // Each icon = 2 armor points (like Minecraft)
    for (let i = 0; i < 10; i++) {
      const { fill } = this.armorIcons[i];
      const pointsForIcon = points - i * 2;
      if (pointsForIcon >= 2) {
        fill.style.background = '#C8C8C8';
        fill.style.opacity = '1';
      } else if (pointsForIcon === 1) {
        fill.style.background = 'linear-gradient(to right, #C8C8C8 50%, transparent 50%)';
        fill.style.opacity = '1';
      } else {
        fill.style.background = 'transparent';
        fill.style.opacity = '0.3';
      }
    }

    // Color-code by total durability state
    const slots = this._armorSystem.slots;
    let totalDur = 0, totalMaxDur = 0;
    for (const slotName of Object.keys(slots)) {
      const piece = slots[slotName];
      if (piece) {
        totalDur += piece.durability || 0;
        totalMaxDur += piece.maxDurability || 1;
      }
    }
    if (totalMaxDur > 0) {
      const ratio = totalDur / totalMaxDur;
      let color = '#C8C8C8'; // default silver
      if (ratio < 0.25) color = '#FF4444'; // red = about to break
      else if (ratio < 0.5) color = '#FFAA00'; // orange = worn
      else if (ratio < 0.75) color = '#FFD700'; // gold = used
      for (let i = 0; i < 10; i++) {
        const { fill } = this.armorIcons[i];
        const pointsForIcon = points - i * 2;
        if (pointsForIcon >= 2) {
          fill.style.background = color;
        } else if (pointsForIcon === 1) {
          fill.style.background = `linear-gradient(to right, ${color} 50%, transparent 50%)`;
        }
      }
    }
  }

  // ─── Connect XP system for automatic updates ────────────────
  setXPSystem(xpSystem) {
    this._xpSystem = xpSystem;
  }

  // ─── Set XP bar visually (0-1 fraction + level number) ─────
  setXP(fraction, level) {
    this.xpBarFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
    this.xpLevel.textContent = String(level ?? 0);
  }
}
